import React, { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { API_IS_SYSTEM_ASSIGNED, API_MY_ATTANDANCE, API_SQE_ATTENDANCE } from "../../config/Endpoints";
import { getApi } from "../../utils/api";
import Cookies from "js-cookie";
import { TailSpin } from "react-loader-spinner";
import { ChevronLeft, ChevronRight, Timer } from "lucide-react";
import { Card } from "../../v2/components/shared/card/card.tsx";
import { Badge, Button, Modal, Select } from "../../v2/components/ui/index.ts";
import { formatLeaveTypeLabel } from "../Leave/utils/leave-type-label.js";
import styles from "./my-attendance.module.css";
import { useUser } from "../../v2/lib/rbac/useUser.ts";
import { isUsEmployeeLocation } from "../../v2/hooks/use-is-us-employee.ts";
import { useSqeLabs } from "../../v2/hooks/use-sqe-labs.ts";

function bucketOfLeaveType(t) {
  if (!t) return null;
  if (t === "Present") return "present";
  if (t === "Absent") return "absent";
  if (
    ["WFH", "WorkFromHome", "workFromHomeLeave", "workFromHomeLeaveForQuarter"].includes(
      t,
    )
  ) {
    return "wfh";
  } 
  if (["HalfDayLeave", "halfDayLeave", "HALF_DAY"].includes(t)) return "halfDay";
  if (t === "Holiday") return "holiday";
  if (t === "Week-off") return null;
  return "leave";
}

const WEEKDAY_LONG = {
  Sun: "Sunday",
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
};

function parseAttendanceDateParts(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return null;
  const parts = dateStr.split(",");
  if (parts.length < 2) return null;
  const dowShort = parts[0].trim();
  const rest = parts[1].trim().split(/\s+/);
  if (rest.length < 2) return null;
  const monthName = rest[0];
  const dayNum = rest[1];
  return {
    dowShort,
    dowLong: WEEKDAY_LONG[dowShort] ?? dowShort,
    monthName,
    dayNum,
  };
} 
 
/** Raw total-hours field from API (snake_case or camelCase). */
function getTotalHourRaw(dayData) {
  if (!dayData) return null;
  return dayData.total_hour ?? dayData.totalHour;
}

function getActiveWorkHoursRaw(dayData) {
  if (!dayData) return null;
  return (
  
    dayData.active_working_hours
 
   
  );
}

/**
 * Human-readable total hours for the day cell pill.
 * API may send: clock `7:23` / `07:23:00`, text `7h 23m`, or decimal hours `7.38` / `7`.
 */
function formatTotalHourDisplay(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (s === "" || s.toUpperCase() === "NA") return null;

  /* Clock-style duration (same convention as dashboard parseHours on total_hour) */
  const clockMatch = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(s);
  if (clockMatch) {
    const h = parseInt(clockMatch[1], 10);
    const mi = parseInt(clockMatch[2], 10);
    const sec = clockMatch[3] != null ? parseInt(clockMatch[3], 10) : 0;
    if (!Number.isFinite(h) || !Number.isFinite(mi) || !Number.isFinite(sec)) return null;
    const totalMinutes = Math.round(h * 60 + mi + sec / 60);
    if (totalMinutes <= 0) return null;
    const outH = Math.floor(totalMinutes / 60);
    const outM = totalMinutes % 60;
    if (outM === 0) return `${outH}h`;
    return `${outH}h ${outM}m`;
  }

  if (/[hm]/i.test(s)) {
    const compact = s.replace(/\s+/g, " ").trim();
    const lowered = compact.replace(/\s/g, "").toLowerCase();
    if (/^0h(0m)?$/.test(lowered) || lowered === "0" || lowered === "0m") return null;
    return compact;
  }
  const num = Number(s);
  if (!Number.isFinite(num) || num <= 0) return null;
  const h = Math.floor(num);
  const m = Math.round((num - h) * 60);
  if (m <= 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** Total time pill: always for Present / WFH / Half day (reference UI); others only when API sends hours. */
function getTotalHourLabelForCell(dayData) {
  if (!dayData) return null;
  const t = dayData.leave_type;
  const formatted = formatTotalHourDisplay(getTotalHourRaw(dayData));
  const tracksHours =
    t === "Present" ||
    ["WFH", "WorkFromHome", "workFromHomeLeave", "workFromHomeLeaveForQuarter"].includes(t) ||
    ["HalfDayLeave", "halfDayLeave", "HALF_DAY"].includes(t);
  if (tracksHours) return formatted ?? "0h";
  return formatted;
}

function getCellSurfaceClass(dayData, isWeekend) {
  const dayClass = isWeekend ? styles.cellOff : styles.cellPlain;
  if (!dayData) return dayClass;

  const t = dayData.leave_type;
  if (t === "Present") return styles.cellPresent;
  if (t === "Absent") return styles.cellAbsent;
  if (t === "Holiday") return styles.cellHoliday;
  if (
    ["WFH", "WorkFromHome", "workFromHomeLeave", "workFromHomeLeaveForQuarter"].includes(t)
  ) {
    return styles.cellWfh;
  }
  if (["HalfDayLeave", "halfDayLeave", "HALF_DAY"].includes(t)) return styles.cellHalfDay;
  if (t === "Week-off") return styles.cellOff;
  /* India + US leave types (Privilege, Casual, Bereavement, PTO, Sick, etc.) and any future leave code */
  return styles.cellLeave;
}

function leaveTypeBadgeVariant(t) {
  const b = bucketOfLeaveType(t);
  if (b === "present") return "success";
  if (b === "absent") return "danger";
  if (b === "wfh") return "neutral";
  if (b === "halfDay") return "warning";
  if (b === "holiday") return "warning";
  if (b === "leave") return "neutral";
  return "neutral";
}

/** Modal badge surface + dot: same token families as calendar cells (cellLeave / cellWfh / cellHoliday). */
function leaveTypeModalBadgeSurfaceClass(t) {
  const b = bucketOfLeaveType(t);
  if (b === "wfh") return styles.modalTypeBadgeWfh;
  if (b === "holiday") return styles.modalTypeBadgeHoliday;
  if (b === "leave") return styles.modalTypeBadgeLeave;
  return undefined;
}

const ABSENT_TIMESHEET_STATUS_MESSAGES = {
  NOT_FILLED: "Timesheet not filled. Please fill and submit it for approval.",
  PENDING: "Timesheet for this day is pending for approval.",
  REJECTED: "Timesheet for this day has been rejected.",
};

function normalizeStatus(value) {
  if (value == null) return "";
  return String(value).trim().toUpperCase();
}

function getTimesheetStatus(dayData) { 
  if (!dayData) return "";
  return normalizeStatus(dayData.timesheet_status);
}

function getAbsentTimesheetMessage(dayData) {
  if (dayData?.leave_type !== "Absent") return null;
  return ABSENT_TIMESHEET_STATUS_MESSAGES[getTimesheetStatus(dayData)] ?? null;
}

function hasPunchTime(value) {
  if (value == null) return false;
  const normalized = String(value).trim().toUpperCase();
  return (
    normalized !== "" &&
    normalized !== "NA" &&
    normalized !== "N/A" &&
    normalized !== "NULL"
  );
}

function hasPunchIn(dayData) {
  return hasPunchTime(dayData?.punch_in_time);
}

function getSqeLeaveTypeLabel(leaveType) {
  const baseType = String(leaveType ?? "").split(/\s+[–—-]\s+/)[0];
  return formatLeaveTypeLabel(baseType);
}

function isWithinThirtyMinuteWindow(value) {
  if (value == null || String(value).trim() === "") return false;
  const minutes = Number(value);
  return Number.isFinite(minutes) && minutes > 0 && minutes <= 30;
}

function getSqeTimingLabel(dayData) {
  if (!dayData || dayData.leave_type === "Absent") return "";

  const timingReason = normalizeStatus(dayData.timing_reason).replace(/[\s-]+/g, "_");
  if (timingReason) {
    if (timingReason === "LATE_COMING") return "Late Coming";
    if (timingReason === "EARLY_LEAVING") return "Early Leaving";
    if (["BOTH", "LATE_COMING_EARLY_LEAVING", "LATE_COMING_AND_EARLY_LEAVING"].includes(timingReason)) {
      return "Late Coming + Early Leaving";
    }
    return "";
  }

  // Older SQE responses did not include timing_reason.
  const late = dayData.late_arrival === true;
  const early = dayData.early_leaving === true;

  if (late && !early) return "Late Coming";
  if (early && !late) return "Early Leaving";
  if (!late) return "";

  const minutesLate = Number(dayData.minutes_late_after_grace);
  const minutesEarly = Number(dayData.minutes_early);
  if (dayData.minutes_late_after_grace == null || dayData.minutes_early == null ||
      !Number.isFinite(minutesLate) || !Number.isFinite(minutesEarly) ||
      minutesLate <= 0 || minutesEarly <= 0) return "";
  const lateInWindow = isWithinThirtyMinuteWindow(minutesLate);
  const earlyInWindow = isWithinThirtyMinuteWindow(minutesEarly);

  if (lateInWindow && earlyInWindow) return "Late Coming + Early Leaving";
  if (!lateInWindow && earlyInWindow) return "Late Coming";
  if (lateInWindow && !earlyInWindow) return "Early Leaving";

  // Both flags are present, but the response does not identify the attendance trigger.
  return "";
}

function getShiftCompleted(dayData, isSQE) {
  if (isSQE && typeof dayData?.shift_completed === "boolean") {
    return dayData.shift_completed;
  }
  return dayData?.is_positive_hour;
}

const MyAttendanceCalendar = ({ selectedEmployee }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [punchData, setPunchData] = useState([]);
  const [isLoading, setIsLoading] = useState();
  const [isSystemAssigned, setIsSystemAssigned] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState({});
  const accessToken = Cookies.get("accessToken");
  const user = useUser();

  const isUsEmployee = useMemo(() => {
    const selectedLocation = [
      selectedEmployee?.location,
      selectedEmployee?.employeeShift,
      selectedEmployee?.personalDetail?.location,
      selectedEmployee?.personalDetail?.employeeShift,
    ].find((location) => String(location ?? "").trim());
    const loggedInLocation = [
      user?.location,
      user?.employeeShift,
      user?.personalDetail?.location,
      user?.personalDetail?.employeeShift,
    ].find((location) => String(location ?? "").trim());

    return isUsEmployeeLocation(selectedLocation ?? loggedInLocation);
  }, [selectedEmployee, user]);

  const isSQE = useSqeLabs();

  const visiblePunchData = useMemo(() => {
    if (isSQE || isUsEmployee) return punchData;

    const isSeptember2026 =
      currentDate.getFullYear() === 2026 && currentDate.getMonth() === 8;
    if (!isSeptember2026) return punchData;

    return punchData.filter(
      (entry) =>
        !(
          entry?.date === "Mon, Sep 07" &&
          String(entry?.leave_type ?? "").trim().toUpperCase() === "HOLIDAY"
        ),
    );
  }, [currentDate, isSQE, isUsEmployee, punchData]);

  useEffect(() => {
    const checkSystemAssigned = async () => {
      try {
        const res = await getApi(API_IS_SYSTEM_ASSIGNED, accessToken);
        setIsSystemAssigned(Boolean(res?.data?.isSystemAssigned));
      } catch (e) {
        console.log(e);
        setIsSystemAssigned(false);
      }
    };

    if (accessToken) {
      checkSystemAssigned();
    } else {
      setIsSystemAssigned(false);
    }
  }, [accessToken]);

  const fetchPunchData = useCallback(async (month, year) => {
    setIsLoading(true);
    const endpoint = isSQE ? API_SQE_ATTENDANCE : API_MY_ATTANDANCE;
    await getApi(
      `${endpoint}?empid=${encodeURIComponent(selectedEmployee?.id)}&month=${encodeURIComponent(month)}&year=${year}`,
      accessToken,
    )
      .then((res) => {
        const attendance = isSQE ? res?.data?.data?.data : res?.data?.data;
        setPunchData(Array.isArray(attendance) ? attendance : []);
        setIsLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setPunchData([]);
        setIsLoading(false);
      });
  }, [accessToken, isSQE, selectedEmployee?.id]);

  useEffect(() => {
    const today = new Date();
    setCurrentDate(today);
    if (selectedEmployee?.id) {
      const currentMonth = today.toLocaleString("en-US", { month: "long" }).toUpperCase();
      void fetchPunchData(currentMonth, today.getFullYear());
    } else {
      setPunchData([]);
    }
  }, [fetchPunchData, selectedEmployee?.id]);

  const goToPreviousMonth = () => {
    const previousMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1,
      1,
    );
    setCurrentDate(previousMonth);
    const currentMonth = new Date(previousMonth)
      .toLocaleString("en-US", { month: "long" })
      .toUpperCase();
    const year = previousMonth.getFullYear();
    fetchPunchData(currentMonth, year);
  };

  const goToNextMonth = () => {
    const nextMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      1,
    );
    setCurrentDate(nextMonth);
    const currentMonth = new Date(nextMonth)
      .toLocaleString("en-US", { month: "long" })
      .toUpperCase();
    const year = nextMonth.getFullYear();
    fetchPunchData(currentMonth, year);
  };

  const monthSelectOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let monthsBack = 0; monthsBack <= 59; monthsBack++) {
      const d = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
      const label = d.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
      const value = `${d.getFullYear()}-${d.getMonth()}`;
      opts.push({ value, label });
    }
    return opts;
  }, []);

  const summaryCounts = useMemo(() => {
    const counts = {
      present: 0,
      absent: 0,
      wfh: 0,
      halfDay: 0,
      leave: 0,
      holiday: 0,
    };
    if (!visiblePunchData?.length) return counts;
    for (const row of visiblePunchData) {
      const b = bucketOfLeaveType(row.leave_type);
      if (b && b in counts) counts[b] += 1;
    }
    return counts;
  }, [visiblePunchData]);

  const currentMonthSelectValue = `${currentDate.getFullYear()}-${currentDate.getMonth()}`;

  const handleMonthSelectChange = (value) => {
    const idx = value.indexOf("-");
    if (idx === -1) return;
    const y = Number(value.slice(0, idx));
    const m = Number(value.slice(idx + 1));
    if (!Number.isFinite(y) || !Number.isFinite(m)) return;
    const d = new Date(y, m, 1);
    setCurrentDate(d);
    const monthUpper = d
      .toLocaleString("en-US", { month: "long" })
      .toUpperCase();
    fetchPunchData(monthUpper, y);
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getWeekday = (year, month, day) => {
    return new Date(year, month, day).getDay();
  };

  const getDayOfWeek = (day, month, year) => {
    const date = new Date(year, month, day);
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return days[date.getDay()];
  };

  const getMonthName = (month) => {
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return monthNames[month];
  };

  const generateCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfWeek = getWeekday(year, month, 1);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const cells = [];
    let day = 1;
    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 7; j++) {
        if ((i === 0 && j < firstDayOfWeek) || day > daysInMonth) {
          cells.push(
            <div key={`${i}-${j}`} className={styles.cellEmpty} aria-hidden />,
          );
        } else {
          const weekday = getWeekday(year, month, day);
          const isWeekend = weekday === 0 || weekday === 6;
          let dayData;
          if (visiblePunchData.length > 0) {
            const dateStr = `${getDayOfWeek(day, month, year)}, ${getMonthName(
              month,
            )} ${day.toString().padStart(2, "0")}`;
            dayData = visiblePunchData.find((d) => d.date === dateStr);
          }

          const timingLabel = isSQE ? getSqeTimingLabel(dayData) : "";
          const cellSurface = timingLabel
            ? styles.cellLate
            : getCellSurfaceClass(dayData, isWeekend);
          const cellDate = new Date(year, month, day);
          cellDate.setHours(0, 0, 0, 0);
          const isToday = cellDate.getTime() === today.getTime();
          const clickable = dayData && (dayData.leave_type !== "Week-off" || timingLabel);
          const totalHourLabel = dayData
            ? getTotalHourLabelForCell(dayData)
            : null;

          cells.push(
            <div
              key={`${i}-${j}`}
              className={clsx(
                styles.cell,
                cellSurface,
                clickable && styles.cellClickable,
                isToday && styles.cellToday,
              )}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={
                clickable
                  ? () => setSelectedAttendance(dayData)
                  : undefined
              }
              onKeyDown={
                clickable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedAttendance(dayData);
                      }
                    }
                  : undefined
              }
            >
              {isToday ? (
                <span className={styles.cellTodayBadge}>Today</span>
              ) : null}
              {timingLabel === "Late Coming" || timingLabel === "Early Leaving" ? (
                <span className={styles.cellLateBadge}>
                  {timingLabel === "Late Coming" ? "LATE" : "EARLY"}
                </span>
              ) : null}
              <span className={styles.cellNumber}>{day}</span>
              <div className={styles.cellLeaveMeta}>
                {totalHourLabel ? (
                  <p className={styles.cellTime}>
                    <span className={styles.cellTimePill}>
                      <Timer
                        size={14}
                        strokeWidth={2}
                        className={styles.cellTimePillIcon}
                        aria-hidden
                      />
                      {totalHourLabel}
                    </span>
                  </p>
                ) : null}
                <p className={styles.cellStatus}>
                  {timingLabel || (dayData
                    ? isSQE
                      ? getSqeLeaveTypeLabel(dayData.leave_type)
                      : formatLeaveTypeLabel(dayData.leave_type)
                    : isWeekend
                      ? "Weekend Off"
                      : "")}
                </p>
              </div>
            </div>,
          );
          day++;
        }
      }
      if (day > daysInMonth) break;
    }
    return cells;
  };

  const nextNavDisabled =
    currentDate.getMonth() >= new Date().getMonth() &&
    currentDate.getFullYear() >= new Date().getFullYear();

  const parsedModalDate = parseAttendanceDateParts(selectedAttendance?.date);
  const statusLabel = isSQE
    ? getSqeLeaveTypeLabel(selectedAttendance?.leave_type)
    : formatLeaveTypeLabel(selectedAttendance?.leave_type);
  const timingLabel = isSQE ? getSqeTimingLabel(selectedAttendance) : "";
  const hasLongStatus = statusLabel.length > 24 || timingLabel.length > 24;
  const absentTimesheetMessage = getAbsentTimesheetMessage(selectedAttendance);
  const showPunchIn = hasPunchIn(selectedAttendance);
  const showPunchOut = hasPunchTime(selectedAttendance?.punch_in_out);
  const totalHoursDisplay = formatTotalHourDisplay(getTotalHourRaw(selectedAttendance));
  const showShiftRow = showPunchIn;
  const showAttendanceDetails =
    selectedAttendance?.leave_type === "Present" ||
    (isSQE && (
      selectedAttendance?.leave_type === "ShortLeave" ||
      showPunchIn || showPunchOut
    ));
  const activeWorkHoursDisplay =
    formatTotalHourDisplay(getActiveWorkHoursRaw(selectedAttendance)) ??
    getActiveWorkHoursRaw(selectedAttendance) ??
    "—";

  return (
    <div className={styles.calendarRoot}>
      {isLoading ? (
        <div className={styles.overlay}>
          <TailSpin
            height="80"
            width="80"
            radius="1"
            color="var(--color-accent-500, #0675d6)"
            ariaLabel="tail-spin-loading"
            visible
          />
        </div>
      ) : null}

      <Card className={styles.headerCard} padding="5xl" variant="default">
        <div className={styles.headerBar}>
          <div className={styles.headerLeading}>
            <div className={styles.headerLeft}>
              <p className={clsx("sub-heading-xs", styles.headerEyebrow)}>
                Attendance report
              </p>
              <h2 className={clsx("heading-xl", styles.headerTitle)}>
                {currentDate.toLocaleString("default", {
                  month: "long",
                  year: "numeric",
                })}
              </h2>
            </div>
            <div className={styles.headerNav}>
              <Button
                type="button"
                variant="muted-outline"
                className={styles.navBtn}
                onClick={goToPreviousMonth}
                aria-label="Previous month"
              >
                <ChevronLeft size={20} strokeWidth={2} aria-hidden />
              </Button>
              <div className={styles.monthSelectWrap}>
                <Select
                  value={currentMonthSelectValue}
                  onChange={handleMonthSelectChange}
                  options={monthSelectOptions}
                  disabled={isLoading}
                  placeholder="Select month"
                />
              </div>
              <Button
                type="button"
                variant="muted-outline"
                className={styles.navBtn}
                onClick={goToNextMonth}
                disabled={nextNavDisabled}
                aria-label="Next month"
              >
                <ChevronRight size={20} strokeWidth={2} aria-hidden />
              </Button>
            </div>
          </div>
          <div className={styles.summaryRow}>
            <Badge variant="success" className={styles.pillPresent}>
              {summaryCounts.present} Present
            </Badge>
            <Badge variant="danger" className={styles.pillAbsent}>
              {summaryCounts.absent} Absent
            </Badge>
            <Badge variant="neutral" className={styles.pillWfh}>
              {summaryCounts.wfh} WFH
            </Badge>
            <Badge variant="warning" className={styles.pillHalfDay}>
              {summaryCounts.halfDay} Half day
            </Badge>
            <Badge variant="neutral" className={styles.pillLeave}>
              {summaryCounts.leave} On leave
            </Badge>
            <Badge variant="neutral" className={styles.pillHoliday}>
              {summaryCounts.holiday} Holiday
            </Badge>
          </div>
        </div>
      </Card>

      <Card className={styles.gridCard} padding="none" variant="default">
        <div className={styles.weekHeader}>
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className={styles.weekHeaderCell}>
              {d}
            </div>
          ))}
        </div>
        <div className={styles.grid}>
          {isLoading === false ? generateCalendar() : null}
        </div>

        <div className={styles.legend} role="list">
          <span className={styles.legendItem} role="listitem">
            <span
              className={clsx(styles.legendDot, styles.legendDotPresent)}
              aria-hidden
            />
            Present
          </span>
          <span className={styles.legendItem} role="listitem">
            <span
              className={clsx(styles.legendDot, styles.legendDotAbsent)}
              aria-hidden
            />
            Absent
          </span>
          {isSQE ? (
            <span className={styles.legendItem} role="listitem">
              <span className={clsx(styles.legendDot, styles.legendDotLate)} aria-hidden />
              Late Coming
            </span>
          ) : null}
          <span className={styles.legendItem} role="listitem">
            <span
              className={clsx(styles.legendDot, styles.legendDotWfh)}
              aria-hidden
            />
            Work from home
          </span>
          <span className={styles.legendItem} role="listitem">
            <span
              className={clsx(styles.legendDot, styles.legendDotHalf)}
              aria-hidden
            />
            Half day
          </span>
          <span className={styles.legendItem} role="listitem">
            <span
              className={clsx(styles.legendDot, styles.legendDotHoliday)}
              aria-hidden
            />
            Holiday
          </span>
          <span className={styles.legendItem} role="listitem">
            <span
              className={clsx(styles.legendDot, styles.legendDotLeave)}
              aria-hidden
            />
            On leave
          </span>
          <span className={styles.legendItem} role="listitem">
            <span
              className={clsx(styles.legendSquare, styles.legendSquareWeekend)}
              aria-hidden
            />
            Weekend
          </span>
        </div>
      </Card> 

      <Modal
        open={Boolean(selectedAttendance?.date)}
        onClose={() => setSelectedAttendance({})}
        title="Attendance Detail"
        size={hasLongStatus ? "md" : "sm"}
      >
        {parsedModalDate ? (
          <div className={styles.modalSummary}>
            <div className={styles.modalHero}>
              <p className={styles.modalHeroWeekday}>{parsedModalDate.dowLong}</p>
              <p className={styles.modalHeroDay}>{parsedModalDate.dayNum}</p>
              <p className={styles.modalHeroMonth}>
                {parsedModalDate.monthName} {currentDate.getFullYear()}
              </p>
            </div>

            <div className={styles.modalStatusStack}>
              <div className={styles.modalStatusRow}>
                <span className={clsx("sub-heading-md", styles.modalRowLabel)}>
                  Status
                </span>
                <Badge
                  
                  variant={leaveTypeBadgeVariant(selectedAttendance?.leave_type)}
                  className={clsx(
                    styles.modalStatusBadge,
                    leaveTypeModalBadgeSurfaceClass(
                      selectedAttendance?.leave_type,
                    ),
                  )}
                >
                  {statusLabel}
                </Badge>
              </div>
              {showShiftRow ? (
                <div className={styles.modalStatusRow}>
                  <span className={clsx("sub-heading-md", styles.modalRowLabel)}>
                    Shift
                  </span>
                  {getShiftCompleted(selectedAttendance, isSQE) ? (
                    <Badge variant="success">Completed</Badge>
                  ) : (
                    <Badge variant="danger">Not-Completed</Badge>
                  )}
                </div>
              ) : null}
              {timingLabel ? (
                <div className={styles.modalStatusRow}>
                  <span className={clsx("sub-heading-md", styles.modalRowLabel)}>
                    Timing
                  </span>
                  <Badge variant="warning" className={styles.modalStatusBadge}>{timingLabel}</Badge>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {absentTimesheetMessage ? (
          <div className={styles.modalTimesheetNotice} role="status">
            <p>{absentTimesheetMessage}</p>
          </div>
        ) : null}

        {showAttendanceDetails ? (
          <>
            {(!isSQE || showPunchIn) ? <div className={styles.modalRow}>
              <span className={clsx("sub-heading-md", styles.modalRowLabel)}>
                Punch in
              </span>
              <span className={clsx("body-sm", styles.modalRowValue)}>
                {selectedAttendance?.punch_in_time}
              </span>
            </div> : null}
            {(!isSQE || showPunchOut) ? <div className={styles.modalRow}>
              <span className={clsx("sub-heading-md", styles.modalRowLabel)}>
                Punch out
              </span>
              <span className={clsx("body-sm", styles.modalRowValue)}>
                {selectedAttendance?.punch_in_out}
              </span>
            </div> : null}
            {(!isSQE || totalHoursDisplay) ? <div className={styles.modalRow}>
              <span className={clsx("sub-heading-md", styles.modalRowLabel)}>
                Total hours
              </span>
              <span className={clsx("body-sm", styles.modalRowValue)}>
                {isSQE
                  ? totalHoursDisplay
                  : totalHoursDisplay ?? getTotalHourRaw(selectedAttendance) ?? "—"}

              </span>
            </div> : null}
            {/* {isSQE && selectedAttendance?.leave_type === "ShortLeave" && (
              <div className={styles.modalRow}>
                <span className={clsx("sub-heading-sm", styles.modalRowLabel)}>
                 Total
                </span>
                <span className={clsx("body-sm", styles.modalRowValue)}>
                  {getAdjustedTotalHours(getTotalHourRaw(selectedAttendance)) ?? "—"}
                </span>
              </div>
            )} */}
            {isSystemAssigned ? (
              <div className={styles.modalRow}>
                <span className={clsx("sub-heading-md", styles.modalRowLabel)}>
                  Active Work Hours
                </span>
                <span className={clsx("body-sm", styles.modalRowValue)}>
                  {activeWorkHoursDisplay}
                </span>
              </div>
            ) : null}
         
          </>
        ) : null}
      </Modal>
    </div>
  );
};

export default MyAttendanceCalendar;
